/**
 * WP 8.4: "I've seen this," and a rating out of five stars — the only file
 * that talks to the `seen` table, the same way every other package in
 * `docs/asks-plan/` gets its own new file rather than adding to `db.ts` or
 * `queries.ts` (neither is this package's to touch; see the plan's README,
 * "Shared files").
 *
 * `useSeen()` reads every row, keyed by `entry_id` — `RateSheet` asks it "is
 * this one already rated" and `Today`/`Browse` ask it "should this row still
 * be in the feed at all" (seen rows leave every list, the same as dismissed
 * ones). `markSeen`/`unmarkSeen` are the two writes; `useMarkSeen`/
 * `useUnmarkSeen` are the mutations `RateSheet` and `RateQueue` call.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './auth'
import { supabase } from './supabase'
import { fetchAll } from './fetchAll'
import { logSignal } from './db'

/** Read off `db.ts`'s own `logSignal` rather than restated, so the two
 *  cannot drift — the same trick `mutations.ts`'s own `Features` uses. */
type SignalFeatures = Parameters<typeof logSignal>[2]

export interface SeenRow {
  entry_id: string
  stars: number | null
  seen_on: string
  note: string | null
}

const SEEN = ['seen'] as const

async function fetchSeen(): Promise<SeenRow[]> {
  const { data, error } = await fetchAll<SeenRow>(() =>
    supabase.from('seen').select('entry_id,stars,seen_on,note').order('entry_id'))
  if (error) throw new Error(error.message)
  return data
}

/** Hoisted, not written inline in `useSeen`. TanStack Query re-runs `select`
 *  whenever the function's identity changes, and an inline arrow is a new
 *  function every render — so the `Map` came back new every render too (a
 *  `Map` is not structurally shared), and Today's `live` memo and Browse's
 *  `base` memo, both keyed on it, re-derived on every interaction anywhere
 *  in the app. With a stable function the result is recomputed only when the
 *  cached rows change. */
const seenByEntry = (rows: SeenRow[]): Map<string, SeenRow> => new Map(rows.map(r => [r.entry_id, r]))

/** Every seen row, keyed by the entry it is about. */
export const useSeen = () => {
  const { session } = useAuth()
  return useQuery({
    queryKey: SEEN,
    queryFn: fetchSeen,
    enabled: !!session,
    select: seenByEntry,
  })
}

/**
 * Upsert on `(user_id, entry_id)`, then the taste signal: `'rated'` with the
 * star count folded into its features where stars is set, `'attended'`
 * (already a taste_signal action — `markWent`'s own) where it is not — "seen
 * it, no opinion" is worth recording as attendance and nothing more.
 *
 * `stars` and `note` are the only columns sent, never `seen_on`: the column's
 * own default only ever fires on the first insert, and re-rating an event
 * must not silently move the date it says it was seen on. `note` is the
 * sheet's own optional line, not part of the brief's three-argument call —
 * the fourth parameter defaults to `null` so every other caller is unchanged.
 */
export async function markSeen(
  entry_id: string, stars: number | null, features: SignalFeatures, note: string | null = null,
) {
  const res = await supabase.from('seen').upsert({ entry_id, stars, note }, { onConflict: 'user_id,entry_id' })
  if (res.error) return res
  await logSignal(entry_id, stars != null ? 'rated' : 'attended', stars != null ? { ...features, stars } : features)
  return res
}

/** "Not seen after all" — a delete, the same reasoning `forgetEntry` gives
 *  for undoing a save: "never decided" and "decided no" (or here, "decided
 *  yes") are different facts, and only one of them belongs in the table. */
export const unmarkSeen = (entry_id: string) => supabase.from('seen').delete().eq('entry_id', entry_id)

export function useMarkSeen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      entryId: string; stars: number | null; features: SignalFeatures; note?: string | null
    }) => {
      const res = await markSeen(vars.entryId, vars.stars, vars.features, vars.note ?? null)
      if (res.error) throw new Error(res.error.message)
      return res
    },
    // `markSeen` logs a `rated`/`attended` taste signal (its own note above)
    // — Phase E4 (K5)'s `['affinity']`, the same reasoning `mutations.ts`'s
    // own `AFFINITY` constant gives.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: SEEN })
      void qc.invalidateQueries({ queryKey: ['affinity'] })
    },
  })
}

export function useUnmarkSeen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (entryId: string) => {
      const res = await unmarkSeen(entryId)
      if (res.error) throw new Error(res.error.message)
      return res
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: SEEN }) },
  })
}
